import { Alert, Select, Stack, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useGetMcbimPipeSeriesQuery } from "../../../../openapi";
import { selectProjectNo } from "../../designSlice";
import {
  selectAutoPipeRoutingPipeDimension,
  selectAutoPipeRoutingPipeSeriesName,
  setAutoPipeRoutingPipeDimensionInRedux,
  setAutoPipeRoutingPipeSeriesNameInRedux,
} from "../../pipeRoutingSlice";

const FALLBACK_PIPE_SERIES = [
  {
    name: "Standard rørserie",
    dimensions: [40],
  },
];

export const AutoPipeRoutingPipeSelectionStep = () => {
  const dispatch = useAppDispatch();
  const projectNo = useAppSelector(selectProjectNo);
  const selectedSeriesName = useAppSelector(selectAutoPipeRoutingPipeSeriesName);
  const selectedDimension = useAppSelector(selectAutoPipeRoutingPipeDimension);
  const { data, isLoading, isError } = useGetMcbimPipeSeriesQuery(
    projectNo ? { projectNo } : skipToken
  );

  const pipeSeries = data != null && data.length > 0 ? data : FALLBACK_PIPE_SERIES;
  const isUsingFallback = data == null || data.length === 0;

  const selectedSeries =
    pipeSeries.find((series) => series.name === selectedSeriesName) ?? pipeSeries[0] ?? null;

  const seriesOptions = useMemo(
    () =>
      pipeSeries.map((series) => ({
        value: series.name,
        label: series.name,
      })),
    [pipeSeries]
  );

  const dimensionOptions = useMemo(
    () =>
      (selectedSeries?.dimensions ?? []).map((dimension) => ({
        value: String(dimension),
        label: `${dimension} mm`,
      })),
    [selectedSeries]
  );

  useEffect(() => {
    if (pipeSeries.length === 0) {
      return;
    }

    const nextSeries = pipeSeries.find((series) => series.name === selectedSeriesName) ?? pipeSeries[0];
    if (nextSeries.name !== selectedSeriesName) {
      dispatch(setAutoPipeRoutingPipeSeriesNameInRedux(nextSeries.name));
    }

    if (
      selectedDimension == null ||
      !nextSeries.dimensions.includes(selectedDimension)
    ) {
      dispatch(setAutoPipeRoutingPipeDimensionInRedux(nextSeries.dimensions[0] ?? null));
    }
  }, [dispatch, pipeSeries, selectedDimension, selectedSeriesName]);

  return (
    <Stack gap="sm">
      {isUsingFallback && !isLoading && (
        <Alert variant="light" color={isError ? "yellow" : "blue"}>
          {isError
            ? "Fant ikke rørserier fra prosjektet. Bruker standardverdier foreløpig."
            : "Ingen rørserier funnet i prosjektet. Bruker standardverdier foreløpig."}
        </Alert>
      )}

      <Select
        label="Rørserie"
        placeholder="Velg rørserie"
        data={seriesOptions}
        value={selectedSeries?.name ?? null}
        onChange={(value) => {
          const nextSeries =
            pipeSeries.find((series) => series.name === value) ?? pipeSeries[0] ?? null;

          dispatch(setAutoPipeRoutingPipeSeriesNameInRedux(nextSeries?.name ?? null));
          dispatch(setAutoPipeRoutingPipeDimensionInRedux(nextSeries?.dimensions[0] ?? null));
        }}
        searchable
        nothingFoundMessage="Ingen rørserier"
      />

      <Select
        label="Dimensjon"
        placeholder="Velg dimensjon"
        data={dimensionOptions}
        value={selectedDimension != null ? String(selectedDimension) : null}
        onChange={(value) => {
          dispatch(setAutoPipeRoutingPipeDimensionInRedux(value != null ? Number(value) : null));
        }}
        disabled={dimensionOptions.length === 0}
        nothingFoundMessage="Ingen dimensjoner"
      />

      <Text size="sm" c="dimmed">
        Velg rørserie og dimensjon som skal brukes videre i oppsettet.
      </Text>
    </Stack>
  );
};
